import React from 'react';
import classNames from 'classnames/bind';
import { Mutation } from '@apollo/client/react/components';
import Button from 'koba/components/Button';
import Icon from 'koba/components/Icon';
import { useCommunityContext } from 'components/CommunityContext';
import { EVENT_POST_TYPES, NUMBER_OF_REACTIONS } from 'utils/constants';
import { rawId } from 'utils/rawId';
import {
  FOLLOW_POST_MUTATION,
  UNFOLLOW_POST_MUTATION,
} from 'schema/Post/mutations';
import { TFunction } from 'next-i18next';
import { AttachmentInput } from 'types/attachment';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';

import styles from './FollowPostButton.module.scss';

const cx = classNames.bind(styles);

type Post = {
  id: string;
  isFollowing: boolean;
  pinnedAt: string;
  postAttachments: Array<AttachmentInput>;
};

interface FollowPostButtonProps {
  post?: Post | null;
  t: TFunction;
  isMobile?: boolean;
  onPostChange(): void;
}

const FollowPostButton: React.FC<FollowPostButtonProps> = ({
  post,
  t,
  isMobile = false,
  onPostChange,
}) => {
  const { trackEvent } = useCommunityContext();

  const postFollowingMutate = (mutate, event) => () => {
    onPostChange();
    mutate({
      variables: {
        id: post?.id,
        numberOfReactions: NUMBER_OF_REACTIONS,
      },
    });

    const attachment = post?.postAttachments ? post?.postAttachments[0] : null;
    const {
      ATTACHMENT,
      PINNED_POST,
      POST_ID,
      POST_ID_ENCODED,
      POST_TYPE,
    } = TrackingProperty;

    trackEvent(event, {
      [ATTACHMENT]: attachment ? attachment.attachmentType : null,
      [PINNED_POST]: !!post?.pinnedAt,
      [POST_ID]: rawId(post?.id || ''),
      [POST_ID_ENCODED]: post?.id,
      [POST_TYPE]: EVENT_POST_TYPES.POST,
    });
  };

  if (post?.isFollowing) {
    return (
      <Mutation mutation={UNFOLLOW_POST_MUTATION} notifyOnNetworkStatusChange>
        {(mutate, { loading }) => (
          <>
            {/* @ts-ignore */}
            <Button
              appearance="ghost"
              className={cx(
                'follow-post__button',
                'follow-post__button--with-icon'
              )}
              dataQA="follow-post__button"
              isDisabled={loading}
              size={isMobile ? 'small' : undefined}
              onClick={postFollowingMutate(mutate, TrackingEvent.UNFOLLOW)}
            >
              <span className={cx('follow-post__button__non-hover')}>
                <Icon
                  className={cx('follow-post__button__non-hover-icon')}
                  name="checkmark"
                />
                {t('Following')}
              </span>
              <span className={cx('follow-post__button__hover')}>
                {t('Unfollow')}
              </span>
            </Button>
          </>
        )}
      </Mutation>
    );
  }
  return (
    <Mutation mutation={FOLLOW_POST_MUTATION} notifyOnNetworkStatusChange>
      {(mutate, { loading }) => (
        <>
          <Button
            appearance="ghost"
            className={cx('follow-post__button')}
            dataQA="follow-post__button"
            isDisabled={loading}
            size={isMobile ? 'small' : undefined}
            onClick={postFollowingMutate(mutate, TrackingEvent.FOLLOW)}
          >
            {t('Follow')}
          </Button>
        </>
      )}
    </Mutation>
  );
};

export default FollowPostButton;
